import { useItemSet } from '@exam-management/common/item-set-provider';
import { SearchQuestionsDocument } from '@exam-management/graphql';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import type { ConfirmationModalProps } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useFormContext } from 'react-hook-form';

import { PublishQuestionModal } from '../../components';
import type { ItemSetQuestionDetailFormInput } from '../../containers';

type PublishItemSetModalProps = Pick<ConfirmationModalProps, 'onClose' | 'open'>;

export default function PublishItemSetModal(props: Readonly<PublishItemSetModalProps>) {
  const { open: isOpen, onClose } = props;

  const { publish, save, isPublishing } = useItemSet();
  const { showSnackbar } = useSnackbar();
  const formContext = useFormContext<ItemSetQuestionDetailFormInput>();

  const handleAccept: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    const isValid = await formContext.trigger();

    if (!isValid) {
      onClose?.(event);
      return;
    }

    await formContext.handleSubmit(async (data) => {
      await save(
        {
          name: data.details.name,
          paragraph: parseContentForApi(data.details.paragraph),
        },
        {
          onError: () => {
            showSnackbar({
              message: 'Save failed, please try again',
              variant: 'error',
            });
          },
        },
      );
    })();

    await publish({
      onCompleted: () => {
        showSnackbar({ message: 'Question has been published', variant: 'success' });
        onClose?.(event);
      },
      onError: () => {
        onClose?.(event);
      },
      refetchQueries: [SearchQuestionsDocument],
    });
  };

  const handleClose: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    onClose?.(event);
  };

  return (
    <PublishQuestionModal
      open={isOpen}
      onAccept={handleAccept}
      onClose={handleClose}
      loading={isPublishing}
    />
  );
}
